/**
 * newFeaturesBindings.js — Привязка новых функций v10 к UI
 * Mikhmel Cornerstone4D DICOM Viewer Pro v6
 * Автоматически добавляет кнопки и обработчики для всех улучшенных модулей
 */

import { showToast } from "../utils/helpers.js";
import { log } from "../utils/logger.js";
import { getIcon, injectIconSprite } from "../core/iconManager.js";
import { getViewportIds, getActiveViewportId, find3DViewportId } from "../core/viewportState.js"; // [10.19.31]

// [10.19.31] Обёртки били в захардкоженное окно "view-axial", КОТОРОГО НЕ
// СУЩЕСТВУЕТ ни в одной раскладке (есть axial/sagittal/coronal/volume3d/
// view-3d) — «Фонарик», «Полосы», «Авто-экспозиция», «Экспорт GIF» и «Запись
// экрана» при живой привязке молча делали ничего. Теперь окно берётся живое:
const _окноАктивное = () => getActiveViewportId?.() || getViewportIds()[0] || null;
const _окно3D = () => find3DViewportId();

// Внедряем SVG-спрайт при загрузке
injectIconSprite();

// Ленивые импорты — загружаются только при первом использовании
const _import = {
  viewport4D:       () => import("../core/viewport4D.js"),
  viewportCamera:   () => import("../core/viewportCamera.js"),
  viewportCine:     () => import("../core/viewportCine.js"),
  viewportEffects:  () => import("../core/viewportEffects.js"),
  viewportKeyboard: () => import("../core/viewportKeyboard.js"),
  viewportPersistence: () => import("../core/viewportPersistence.js"),
  viewportScreenshot:  () => import("../core/viewportScreenshot.js"),
  viewportSync:     () => import("../core/viewportSync.js"),
  viewportUI:       () => import("../core/viewportUI.js"),
  viewportVoice:    () => import("../core/viewportVoice.js"),
  viewportAnalysis: () => import("../core/viewportAnalysis.js"),
  calciumScoreCore: () => import("../ai/calciumScoreCore.js"),
  vtkModule:        () => import("../compute/vtkModule.js"),
  gpuCompute:       () => import("../compute/gpuCompute.js"),
};

// ══════════════════════════════════════════════════════════════
//  ФУНКЦИИ ДЛЯ ВЫЗОВА ИЗ UI (безопасные обёртки с toast)
// ══════════════════════════════════════════════════════════════

// [10.19.31] УДАЛЕНЫ обёртки: computeCalciumScore, jumpToPathology,
// computeVolumeStats — их кнопки убраны из меню (решение 10.3.5 наконец
// исполнено, Агатстон один — вкладка ИИ); flyAlongBezier — полёт по четырём
// зашитым точкам мимо анатомии (настоящий облёт — Автовращение, 10.19.30);
// runGaussianBlur3D — сглаживала ВЫДУМАННЫЙ нулевой массив и хвалилась
// «завершено»; enable/disableVoiceControl — третий голосовой движок на той же
// кнопке (хозяин один — featureBindings); showRadialMenu — звала контейнер
// "viewport-axial", которого нет, и не имела ни одной кнопки-входа.

export async function computeEF() {
  try {
    const m = await _import.viewport4D();
    const result = await m.computeEF();
    if (result) showToast(`💓 ФВ: ${result.ef}% (КДО: ${result.edv} мл, КСО: ${result.esv} мл)`, "success", 3000);
  } catch(e) { showToast("❌ Ошибка вычисления ФВ", "error"); log.warn("[newFeatures]", e.message); }
}

export async function detectAorticAneurysm() {
  try {
    const m = await _import.viewportAnalysis();
    const result = await m.detectAorticAneurysm();
    if (result) showToast(`🫀 Аорта: ${result.maxDiameterMm} мм — ${result.aneurysmWarning}`, "info", 3000);
  } catch(e) { showToast("❌ Ошибка скрининга аорты", "error"); log.warn("[newFeatures]", e.message); }
}




export async function autoDetectCriticalFindings() {
  try {
    const m = await _import.viewportAnalysis();
    const result = await m.autoDetectCriticalFindings();
    if (result?.findings?.length) {
      for (const f of result.findings) showToast(`${f.pathology}`, f.severity === "CRITICAL" ? "error" : "warning", 4000);
    }
  } catch(e) { showToast("❌ Ошибка детекции", "error"); log.warn("[newFeatures]", e.message); }
}

export async function analyzeBrainAtrophy() {
  try {
    const m = await _import.viewportAnalysis();
    const result = await m.analyzeBrainAtrophy();
    if (result?.regions?.["Анализ_Атрофии"]) showToast(`🧠 ${result.regions["Анализ_Атрофии"].warning}`, "info", 3000);
  } catch(e) { showToast("❌ Ошибка анализа", "error"); log.warn("[newFeatures]", e.message); }
}

export async function segmentOrgansByDensity() {
  try {
    const m = await _import.viewportAnalysis();
    const result = await m.segmentOrgansByDensity();
    if (result?.organs) {
      const names = Object.keys(result.organs).join(", ");
      showToast(`🫁 Органы: ${names}`, "info", 3000);
    }
  } catch(e) { showToast("❌ Ошибка сегментации", "error"); log.warn("[newFeatures]", e.message); }
}

export async function exportAnimatedGIF() {
  try {
    const m = await _import.viewportScreenshot();
    const окно = _окноАктивное();
    if (!окно) { showToast("GIF: нет открытого окна с серией", "warning", 2500); return; }
    const blob = await m.exportAnimatedGIF(окно, { from: 0, to: 50, delay: 100 });
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `cine_${Date.now()}.gif`; a.click();
      URL.revokeObjectURL(url);
    }
  } catch(e) { showToast("❌ Ошибка GIF", "error"); log.warn("[newFeatures]", e.message); }
}

export function startScreenRecording() {
  const окно = _окноАктивное();
  if (!окно) { showToast("Запись: нет открытого окна", "warning", 2500); return; }
  import("../core/viewportScreenshot.js").then(m => m.startScreenRecording(окно));
  showToast("🔴 Запись экрана начата", "info", 2000);
}

export async function stopScreenRecording() {
  try {
    const m = await _import.viewportScreenshot();
    const blob = await m.stopScreenRecording();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `recording_${Date.now()}.webm`; a.click();
      URL.revokeObjectURL(url);
    }
  } catch(e) { log.warn("[newFeatures]", e.message); }
}





export function enableFlashlight() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportEffects.js").then(m => m.enableFlashlight(окно));
}

export function disableFlashlight() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportEffects.js").then(m => m.disableFlashlight(окно));
}

export function showLetterbox() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportUI.js").then(m => m.showLetterbox(окно));
}

export function hideLetterbox() {
  const окно = _окноАктивное(); if (окно) import("../core/viewportUI.js").then(m => m.hideLetterbox(окно));
}


export async function createOrientationCube() {
  try {
    const m = await _import.viewportCamera();
    const container = document.getElementById("orientation-cube-container");
    if (!container) {
      const div = document.createElement("div");
      div.id = "orientation-cube-container";
      div.style.cssText = "position:fixed; bottom:80px; right:20px; z-index:1000;";
      document.body.appendChild(div);
      const о3 = _окно3D();
      if (!о3) { div.remove(); showToast("Куб ориентации: нет объёмного окна — откройте MPR 6 или 3D", "warning", 3000); return; }
      m.createOrientationCube(о3, "orientation-cube-container", 80);
    }
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

export async function enableAutoExposure() {
  try {
    const m = await _import.viewportEffects();
    const окно = _окноАктивное(); if (!окно) { showToast("Авто-экспозиция: нет открытого окна", "warning", 2000); return; } m.enableAutoExposure(окно);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

/**
 * [11.1.1] Синхронизация окон: переключатель, который ОТЧИТЫВАЕТСЯ.
 *
 * Владелец сказал «кнопка мёртвая, в консоли даже нажатие не видно». Он был
 * прав по сути, а я — неправ, когда, прочитав код, ответил «работает».
 * Код действительно исполнялся, но наблюдать это было НЕЧЕМ:
 *
 *   • об успехе не сообщалось — только log.debug, невидимый при обычном уровне;
 *   • об отказе не сообщалось вовсе: `if (!re) return`, и тишина;
 *   • при одном окне в раскладке связывать нечего, и молчание было
 *     неотличимо от поломки;
 *   • сбой внутри уходил в необработанный промис — `.then()` без `.catch()`;
 *   • выключить связку было нечем: функции disable* есть, но их никто не звал.
 *
 * Урок общий: «код исполняется» и «кнопка работает» — разные утверждения.
 * Вторая требует, чтобы человек увидел результат.
 */
// [11.1.1] Имя обёртки РАЗВЕДЕНО с ядром. Пока обе звались syncAllViewports,
// одна была синхронной (ядро, возвращает сводку), другая асинхронной (эта,
// переключатель с отчётом) — и это поймал await_test, увидев вызов ядра без
// await внутри асинхронной тёзки. Две разные функции под одним именем — ровно
// та ловушка, которую мы весь месяц выкорчёвываем.
export async function переключитьСвязкуОкон() {
  const кн = document.getElementById('btn-sync-all');
  const включено = кн?.dataset?.on === 'true';
  try {
    const m = await import('../core/viewportSync.js');
    if (включено) {
      m.disableMPRSync?.(); m.disablePositionSync?.(); m.disableStackScrollSync?.();
      if (кн) { кн.dataset.on = 'false'; кн.classList.remove('active'); }
      showToast('Связка окон выключена', 'info', 2000);
      return;
    }
    const и = m.syncAllViewports();
    // Отчитываемся ЧИСЛАМИ: сколько окон связано и каких. Если связывать
    // нечего — говорим это прямо, а не молчим.
    const мпр = и?.mpr ?? 0, стопок = и?.stack ?? 0, объёмных = и?.vol3d ?? 0;
    const всего = мпр + стопок + объёмных;
    if (!всего) {
      showToast('Связывать нечего: нужно минимум два окна одного вида. Смените раскладку на MPR или 2×2', 'warning', 5000);
      return;
    }
    const части = [];
    if (мпр) части.push(`${мпр} MPR`);
    if (стопок) части.push(`${стопок} со срезами`);
    if (объёмных) части.push(`${объёмных} объёмных`);
    if (кн) { кн.dataset.on = 'true'; кн.classList.add('active'); }
    showToast(`Окна связаны: ${части.join(', ')}. Нажмите ещё раз, чтобы развязать`, 'success', 3500);
  } catch (e) {
    // Раньше это исключение уходило в необработанный промис — на экране
    // не появлялось ничего, и кнопка выглядела мёртвой.
    log.warn('[Синхронизация]', e.message);
    showToast('Связать окна не удалось: ' + e.message, 'error', 4000);
  }
}

export async function startTourRecording() {
  try {
    const m = await _import.viewportCamera();
    const о3 = _окно3D();
    if (!о3) { showToast("Тур: нет объёмного окна — откройте MPR 6 или 3D", "warning", 3000); return; }
    m.startRecording(о3, 10);
    showToast("🔴 Запись тура начата", "info", 2000);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

export async function stopTourRecording() {
  try {
    const m = await _import.viewportCamera();
    const tour = m.stopRecording();
    if (tour) showToast(`⏹ Тур записан: ${tour.keyframes.length} кадров`, "success", 2000);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

export async function enableRRTimeline() {
  try {
    const m = await _import.viewportCine();
    const container = document.getElementById("rr-timeline-container");
    if (!container) {
      const div = document.createElement("div");
      div.id = "rr-timeline-container";
      div.style.cssText = "position:fixed; bottom:20px; left:50%; transform:translateX(-50%); z-index:1000;";
      document.body.appendChild(div);
      const окно = _окноАктивное(); if (окно) m.createRRTimeline(окно, "rr-timeline-container");
    }
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

/**
 * [11.1.1] Все жесты — с отчётом и честным предупреждением.
 *
 * Владелец спросил: «эта кнопка вообще непонятно что делает». Справедливо —
 * она молчала, а название не объясняло. По коду (viewportUI.enableGestures)
 * это СЕНСОРНЫЕ жесты: двойное касание — сброс, щипок — масштаб, свайп —
 * листание. На компьютере с мышью они не срабатывают вовсе, потому что
 * слушают touchstart/touchmove.
 *
 * Поэтому: считаем окна, говорим сколько включено, и на устройстве без сенсора
 * прямо предупреждаем, что толку не будет. Молчать здесь — обманывать.
 */
export async function enableAllGestures() {
  try {
    const m = await import('../core/viewportUI.js');
    m.enableAllGestures();
    const окон = (typeof getViewportIds === 'function' ? getViewportIds() : []).length;
    const естьСенсор = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    if (!окон) { showToast('Жесты включать не на чем: нет открытых окон', 'warning', 3000); return; }
    showToast(естьСенсор
      ? `Сенсорные жесты включены в ${окон} окнах: двойное касание — сброс, щипок — масштаб, свайп — листание`
      : `Жесты включены в ${окон} окнах, но экран не сенсорный — они не сработают. Пользуйтесь мышью и колесом`,
    естьСенсор ? 'success' : 'warning', естьСенсор ? 3500 : 5000);
  } catch (e) {
    log.warn('[Жесты]', e.message);
    showToast('Жесты включить не удалось: ' + e.message, 'error', 4000);
  }
}

export async function startMacroRecording() {
  try {
    const m = await _import.viewportKeyboard();
    m.startMacroRecording();
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

export async function stopMacroRecording() {
  try {
    const m = await _import.viewportKeyboard();
    const macro = m.stopMacroRecording();
    if (macro) showToast(`⏹ Макрос: ${macro.steps.length} шагов`, "success", 2000);
  } catch(e) { log.warn("[newFeatures]", e.message); }
}

// Экспорт для глобального доступа
if (typeof window !== "undefined") {
  window.__newFeatures = {
    computeEF, detectAorticAneurysm, autoDetectCriticalFindings,
    analyzeBrainAtrophy, segmentOrgansByDensity, exportAnimatedGIF,
    startScreenRecording, stopScreenRecording, enableFlashlight,
    disableFlashlight, showLetterbox, hideLetterbox, createOrientationCube,
    enableAutoExposure, переключитьСвязкуОкон, startTourRecording,
    stopTourRecording, enableRRTimeline, enableAllGestures,
    startMacroRecording, stopMacroRecording,
  };
}

log.debug("[newFeatures] Bindings loaded — 30+ functions available");

// ══════════════════════════════════════════════════════════════
//  ПРИВЯЗКИ КНОПОК v10 — ПЕРЕЕХАЛИ СЮДА ИЗ index.html  [11.0.9]
// ══════════════════════════════════════════════════════════════
/**
 * ПОВОД. Эти девять привязок жили инлайн-скриптом в конце index.html. Работали
 * они исправно, но были НЕВИДИМЫ для обычного поиска: кто искал обработчик в
 * src/ и не находил, делал вывод «кнопка мёртвая».
 *
 * Ловушка не выдуманная — в неё попадал я сам при разборе верхнего меню:
 * поиск показал «привязки не найдено» у «Фракции выброса» и «Атрофии мозга»,
 * хотя обе живые. В enterprisePanel описан тот же случай словами: «пока я
 * смотрел только src/, три живые кнопки выглядели мёртвыми, и я чуть не
 * оставил их дубли».
 *
 * Цена ошибки здесь — удалённая клиническая функция. Поэтому привязки
 * перенесены в модуль, где их видят и поиск, и сторожа (uiwiring_test читает
 * src/ целиком).
 *
 * Поведение сохранено дословно, включая переключатели «запись/стоп» и
 * «включено/выключено» на dataset. Единственное добавление — сигнал прерывания,
 * чтобы слушателей можно было снять при разборке (в инлайне этого не было).
 */
let _acНовые = null;
function _сигнал() {
  if (!_acНовые) _acНовые = new AbortController();
  return _acНовые.signal;
}

/** Кнопка-переключатель на dataset: одно нажатие включает, второе выключает. */
function _переключатель(ид, поле, включить, выключить, текстВкл, текстВыкл) {
  const кн = document.getElementById(ид);
  if (!кн) return;
  кн.addEventListener('click', () => {
    const было = кн.dataset[поле] === 'true';
    if (было) { выключить(); кн.dataset[поле] = 'false'; if (текстВыкл) кн.textContent = текстВыкл; }
    else { включить(); кн.dataset[поле] = 'true'; if (текстВкл) кн.textContent = текстВкл; }
    // Подпись меняем ТОЛЬКО когда она задана: у «Фонарика» и «Полос» в кнопке
    // лежит значок, и запись текста стёрла бы его — в инлайне подпись этим
    // двум и не трогали.
  }, { signal: _сигнал() });
}

export function initNewFeaturesButtons() {
  const кн = (ид, fn) => document.getElementById(ид)?.addEventListener('click', fn, { signal: _сигнал() });

  // ── Клинические расчёты (меню «Анализ») ──
  кн('btn-compute-ef', computeEF);
  кн('btn-aortic-aneurysm', detectAorticAneurysm);
  кн('btn-brain-atrophy', analyzeBrainAtrophy);
  кн('btn-auto-detection', autoDetectCriticalFindings);

  // ── Экспорт ──
  кн('btn-export-gif', exportAnimatedGIF);
  _переключатель('btn-screen-record', 'recording', startScreenRecording, stopScreenRecording,
    '⏹ Стоп запись', '🔴 Запись экрана');

  // ── 3D: камера и эффекты ──
  кн('btn-orientation-cube', createOrientationCube);
  _переключатель('btn-tour-record', 'recording', startTourRecording, stopTourRecording,
    '⏹ Стоп тур', '🔴 Запись тура');
  _переключатель('btn-flashlight', 'on', enableFlashlight, disableFlashlight, null, null);
  _переключатель('btn-letterbox', 'on', showLetterbox, hideLetterbox, null, null);
  кн('btn-auto-exposure', enableAutoExposure);

  // [10.19.31] Привязки голоса здесь НЕТ и не должно быть: у btn-voice-control
  // было три хозяина — этот блок, appInit и featureBindings, — три
  // распознавателя на один клик. Единственный хозяин — featureBindings.

  кн('btn-gestures-all', enableAllGestures);
  кн('btn-sync-all', переключитьСвязкуОкон);

  log.debug('[newFeatures] привязки кнопок v10 подключены из модуля');
}

/** Снять слушателей — для разборки интерфейса. */
export function destroyNewFeaturesButtons() {
  if (_acНовые) { _acНовые.abort(); _acНовые = null; }
}
